import {
    Dispatch,
    SetStateAction,
    useCallback,
    useDebugValue,
    useMemo,
    useReducer,
} from "react";
import { ZoomElement, zoomEquals } from "../Models/ZoomElement";

const VERBOSE: boolean = false;

export enum ActionKind {
    /** remove all zoom history and start form a null zoom */
    Reset,
    /** add a new zoom to the history */
    Set,
    /** go back to the zoom before the current one */
    Back,
    /** go back to the first zoom in the history */
    Initial,
}

interface ZoomState {
    /** the current zoom */
    zoom: ZoomElement;
    /** add a new zoom to the history */
    setZoom: Dispatch<SetStateAction<ZoomElement | null>>;
    /** remove all zoom history and start form a null zoom */
    resetZoom: () => void;
    /** go back to the zoom before the current one */
    zoomBack: () => void;
    /** go back to the first zoom in the history */
    backToInitialZoom: () => void;
}

type Action =
    | {
        kind: Exclude<ActionKind, ActionKind.Set>;
    }
    | {
        kind: ActionKind.Set;
        value: SetStateAction<ZoomElement | null>;
    };

/**
 * the state type for the history reducer
 */
interface State {
    /** the array of visited zooms, note that when "zoom back" is pressd the previous zooms are left at the end of the array and only index is changed in case we need need to implement "zoom forward" in the future */
    history: ZoomElement[];
    /** the index of the current zoom in the history array */
    index: number;
}

const defaultHistory: State = {
    history: [],
    index: -1,
};

/** format dates in the zoom's x axis to increase readability for debugging */
const formatZoom = (
    zoom: ZoomElement | null
): { x: string[]; y: number[] } | null => {
    return (
        zoom && {
            x: zoom.x.map((dateTime) => dateTime.toISO()),
            y: zoom.y,
        }
    );
};

/** format dates in the zooms' x axis to increase readability for debugging */
const formatHistory = ({ history, index }: State) => {
    const current = formatZoom(history[index] ?? null);
    return { history: history.map(formatZoom), index, current };
};

/** compute the new zoom given the current one and a SetStateAction */
function getNewZoom(
    currentZoom: ZoomElement | null,
    value: SetStateAction<ZoomElement | null>
) {
    let newZoom: ZoomElement | null;
    if (typeof value === "function") {
        newZoom = value(currentZoom);
    } else {
        newZoom = value;
    }
    return newZoom;
}

/** compute the new history given the current one and a SetStateAction for the current zoom value */
const getNewState = (
    previous: State,
    value: SetStateAction<ZoomElement | null>
): State => {
    const { history, index } = previous;
    const currentZoom = history[index] ?? null;
    let newZoom: ZoomElement | null = getNewZoom(currentZoom, value);
    if (
        // no new zoom is provided
        !newZoom ||
        // the new zoom is identical to the current one
        (currentZoom && zoomEquals(currentZoom, newZoom))
    ) {
        return previous;
    }
    // remove all history entries after the current one and replace them with newZoom
    const newHistory = [...history];
    const newIndex = index + 1;
    newHistory.splice(newIndex, history.length - newIndex, newZoom);
    const newState = { history: newHistory, index: newIndex };
    VERBOSE &&
        console.log(
            "at useZoom setZoom getNewState, newState:",
            formatHistory(newState)
        );
    return newState;
};

/**
 * useZoom handles the zoom history in a centralized manner. It keeps track of the current zoom as well as the previously selected ones and offers callbacks useful for manipulating the zoom history, such as erasing it, adding one zoom, going back one step or to the initial zoom.
 * @returns
 */
export const useZoom = (): ZoomState => {
    const [state, dispatch] = useReducer(
        (previous: State, action: Action): State => {
            const { history, index } = previous;
            if (action.kind === ActionKind.Reset) {
                return defaultHistory;
            } else if (action.kind === ActionKind.Set) {
                return getNewState(previous, action.value);
            } else if (action.kind === ActionKind.Back) {
                if (index > 0) {
                    return {
                        history,
                        index: index - 1,
                    };
                } else {
                    // cannot go further back
                    return previous;
                }
            } else if (action.kind === ActionKind.Initial) {
                if (history.length > 0 && index > 0) {
                    return {
                        history,
                        index: 0,
                    };
                } else {
                    // there is no initial zoom to set
                    return previous;
                }
            }
            // assert that the cases above are exhaustive
            let neverKind: never = action.kind;
            console.warn(
                "at useZoom useReducer, expected kind to be in ActionKind, found",
                neverKind
            );
            return previous;
        },
        defaultHistory
    );

    useDebugValue(state, formatHistory);

    const zoom = useMemo(() => {
        const { history, index } = state;
        return history[index] ?? null;
    }, [state]);

    const setZoom: Dispatch<SetStateAction<ZoomElement | null>> = useCallback(
        (action) => {
            dispatch({
                kind: ActionKind.Set,
                value: action,
            });
        },
        [dispatch]
    );

    const zoomBack = useCallback(() => {
        VERBOSE &&
            console.log("at useZoom zoomBack, state:", formatHistory(state));
        dispatch({ kind: ActionKind.Back });
    }, [dispatch]);

    const resetZoom = useCallback(() => {
        VERBOSE &&
            console.log("at useZoom resetZoom, state:", formatHistory(state));
        dispatch({ kind: ActionKind.Reset });
    }, [dispatch]);

    const backToInitialZoom = useCallback(() => {
        VERBOSE &&
            console.log(
                "at useZoom backToInitialZoom, state:",
                formatHistory(state)
            );
        dispatch({ kind: ActionKind.Initial });
    }, [dispatch]);

    return { zoom, setZoom, resetZoom, zoomBack, backToInitialZoom };
};
